import React from 'react';
import { makeStyles, Typography, Box, Tooltip, Button } from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import { withTranslation, Router } from 'config/i18n';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import Link from 'components/shared/Link';
import ChatIcon from '@material-ui/icons/Chat';
import useTalkJS from 'common/hooks/useTalkJs';
import { useLayoutState, useLayoutSnackbar } from 'components/shared/Layout';

const useStyles = makeStyles((theme) => ({
  user: {
    display: 'flex',
    alignItems: 'center',

    '&.userCentered': {
      [theme.breakpoints.down('sm')]: {
        alignItems: 'center',
        flexDirection: 'column',
      },
    },
  },
  userImage: {
    width: theme.spacing(5.5),
    height: theme.spacing(5.5),
    borderRadius: '50%',
    overflow: 'hidden',
    marginRight: theme.spacing(1),
    display: 'inline-flex',
    justifyContent: 'center',
    flexShrink: '0',

    '& img': {
      display: 'block',
      width: 'auto',
      height: '100%',
    },

    [theme.breakpoints.up('md')]: {
      width: theme.spacing(8.75),
      height: theme.spacing(8.75),
      marginRight: theme.spacing(2),
    },

    '.userCentered &': {
      [theme.breakpoints.down('sm')]: {
        marginRight: '0',
        marginBottom: theme.spacing(1.5),
      },
    },
  },
  userName: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.secondary.light,

    '& svg': {
      marginLeft: theme.spacing(1.25),
      color: theme.palette.info.main,
      height: theme.typography.pxToRem(20),
    },

    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(20),
    },

    '.userCentered &': {
      justifyContent: 'center',
    },
  },
  badges: {
    display: 'flex',
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',

    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0.5),
    },

    '&.centered': {
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'center',
      },
    },
  },
  badge: {
    backgroundColor: theme.palette.grey['50'],
    border: `1px solid ${theme.palette.grey['100']}`,
    padding: theme.spacing(0.375, 1.125),
    borderRadius: theme.typography.pxToRem(11),
    display: 'flex',
    alignItems: 'center',
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 'normal',
    whiteSpace: 'nowrap',

    '& svg': {
      height: theme.typography.pxToRem(16),
      width: theme.typography.pxToRem(16),
      marginLeft: theme.spacing(0.5),
      color: theme.palette.warning.main,
    },

    '&:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  badge2: {
    backgroundColor: 'transparent',
    color: theme.palette.secondary.dark,

    '& svg': {
      color: theme.palette.grey['900'],
    },
  },
  date: {
    color: theme.palette.grey['200'],
    fontWeight: '500',
    lineHeight: theme.typography.pxToRem(14),
    marginTop: theme.spacing(0.5),

    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(1),
    },
  },
  img: {
    display: 'block',
    width: 'auto',
    height: '100%',
  },
}));

const UserInfoSubInfo = (props) => {
  const {
    t,
    image,
    name,
    date,
    rate,
    trips,
    centered,
    commercialHostToolTip,
    joinedOnDate,
    verified,
    moreInfoPath,
    translatePath = true,
    showMessageBtn,
    ownerUserId
  } = props;
  const classes = useStyles();
  const { openTalkJsPopup } = useTalkJS();
  const { layoutState } = useLayoutState();
  const [snackbar, setSnackbar] = useLayoutSnackbar();
  const currentUser = layoutState.current_user;

  const handleMessageBtnClick = () => {
    if (currentUser) {
      openTalkJsPopup({
        user_id: ownerUserId,
        name,
        photoUrl: image,
        role: 'seller',
      });
    } else {
      setSnackbar({
        ...snackbar,
        open: true,
        severity: 'info',
        message: t('fe_es_user_info_sub_info_component:sign_in_up_first'),
      });
      Router.push(`/users/sign_in?return_url=${window.location.pathname}`);
    }
  };

  return (
    <>
      <Box>
        <Box className={`${classes.user} ${centered ? 'userCentered' : ''}`}>
          <Box className={classes.userImage}>
            {moreInfoPath ? (
              <Box className={classes.img}>
                <Link href={moreInfoPath} translate={translatePath}>
                  <img src={image || '/imgs/placeholder_user2.webp'} alt="user-avatar" />
                </Link>
              </Box>
            ) : (
              <img src={image || '/imgs/placeholder_user2.webp'} alt="user-avatar" />
            )}
          </Box>

          <Box className={classes.userInfo}>
            <Box className={classes.userName}>
              {name}
              {verified && <VerifiedUserIcon />}
            </Box>

            {joinedOnDate && (
              <Typography variant="caption" component="div" className={classes.date}>
                {t('fe_es_user_info_sub_info_component:joined_on')} {joinedOnDate}
              </Typography>
            )}

            {date && (
              <Typography variant="caption" component="div" className={classes.date}>
                {date}
              </Typography>
            )}
          </Box>
        </Box>
        {(!!rate || !!trips || commercialHostToolTip || showMessageBtn) && (
          <Box className={`${classes.badges} ${centered ? 'centered' : ''}`}>
            {(rate !== undefined && rate !== null && rate > 0) && (
              <span className={classes.badge}>
                <span>{rate}</span>

                <StarIcon />
              </span>
            )}

            {trips > 0 && trips !== '' && (
              <span className={classes.badge}>
                {`${trips} `}
                {t('fe_es_user_info_sub_info_component:trips')}
              </span>
            )}

            {commercialHostToolTip && (
              <Tooltip
                title={t('fe_es_user_info_sub_info_component:commercial_host_tooltip')}
                enterTouchDelay="50"
              >
                <span className={`${classes.badge} ${classes.badge2}`}>
                  {t('fe_es_user_info_sub_info_component:commercial_host')}

                  <HelpOutlineIcon />
                </span>
              </Tooltip>
            )}
            {showMessageBtn && (
              <Box ml={1}>
                <Button startIcon={<ChatIcon />} color="default" onClick={handleMessageBtnClick}>
                  {t('fe_es_user_info_sub_info_component:message')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default withTranslation('fe_es_user_info_sub_info_component')(UserInfoSubInfo);
